import React, { useState } from 'react';
import { useEstimateJobTag } from '../../context/EstimateJobTagContext';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { SaveIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLoading } from '../../context/LoadingContext';

const EstimateJobTagForm = ({ tagToEdit, onClose, onSuccess }) => {
  const { updateEstimateJobTag, addEstimateJobTag, formErrors, clearErrors } = useEstimateJobTag();
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { user } = useAuth();
   
  const schema = yup.object().shape({
    name: yup.string().required('Tag name is required'),
    active: yup.boolean().default(true)
  });

  const getDefaultValues = (tagToEdit = {}, user = {}) => ({
    name: tagToEdit?.name || '',
    active: tagToEdit?.active ?? true
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(tagToEdit, user)
  });

  // Reset form when tagToEdit changes
  React.useEffect(() => {
    if (tagToEdit) {
      reset(getDefaultValues(tagToEdit, user));
    } else {
      reset(getDefaultValues({}, user));
    }
  }, [tagToEdit, reset, user]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading();
    try {
      if (tagToEdit) {
        // Update existing estimation job tag
        const response = await updateEstimateJobTag(tagToEdit.id, data);
        
        if (response.status === 200) {
          toast.success('Estimate Job Tag updated successfully!');
          reset();
          if (onSuccess) onSuccess();
        } else {
          if (response.errors) {
            Object.entries(response.errors).forEach(([field, message]) => {
              setError(field, {
                type: 'manual',
                message,
              });
            });
          } else if (response.error?.message) {
            setError('general', {
              type: 'manual',
              message: response.error.message,
            });
          }
        }
        setIsLoading(false);
        hideLoading();
      } else {
        // Create new estimation job tag
        const response = await addEstimateJobTag(data);
        if (response.status === 200) {
          toast.success('Estimate Job Tag created successfully!');
          reset();
          if (onSuccess) onSuccess();
        } else {
          if (response.errors) {
            Object.entries(response.errors).forEach(([field, message]) => {
              setError(field, {
                type: 'manual',
                message,
              });
            });
          } else if (response.error?.message) {
            setError('general', {
              type: 'manual',
              message: response.error.message,
            });
          }
        }
        setIsLoading(false);
        hideLoading();
      }
    } catch (error) {
      setIsLoading(false);
      hideLoading();
      console.error('Error saving estimate job tag:', error);
      toast.error('Failed to save estimate job tag. Please try again.', {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

        {formErrors && (
          (typeof formErrors === 'string' || formErrors.general) ? (
        
            <div className="  text-red-700 px-4 py-3 mb-3 rounded relative mt-2" role="alert">
              <button
                type="button"
                className="absolute top-0 right-0 mt-2 mr-3 text-red-700 font-bold text-xl leading-none focus:outline-none"
                onClick={() => clearErrors()}
                aria-label="Close"
              >
                &times;
              </button>
              {!formErrors.general && (<strong className="font-bold">Error: </strong>)}
              <span className="block sm:inline">{formErrors.general || formErrors}</span>
            </div>
          ) : ''
        )}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Tag Name *
        </label>
        <input
          type="text"
          id="name"
          {...register('name')}
          className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
            errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
          }`}
          placeholder="Enter Tag Name"
        />
        {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="active"
          {...register('active')}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
          Active
        </label>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600 text-sm">{errors.general.message}</p>
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SaveIcon className="w-4 h-4 mr-1" />
          {isLoading ? 'Saving...' : (tagToEdit ? 'Update' : 'Save')}
        </button>
      </div>
    </form>
  );
};

export default EstimateJobTagForm; 